import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchSalonProfile, updateSalonProfile } from '../../api/salon';
import Input from '../../components/ui/Input';
import DashboardShell from '../../components/ui/DashboardShell';
import BankAccountsManager from '../../components/payments/BankAccountsManager';
import { AUDIENCE_TYPES } from '../../constants/audienceTypes';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

const SalonSettingsPage = () => {
  const { t } = useTranslation();
  const { role } = useAuth();
  const { showToast } = useToast();
  const canManageBank = ['SALON_MANAGER', 'SUPER_ADMIN'].includes(role);
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['salon-profile'],
    queryFn: fetchSalonProfile,
  });

  const [businessName, setBusinessName] = useState('');
  const [tagline, setTagline] = useState('');
  const [description, setDescription] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [audienceType, setAudienceType] = useState('UNISEX');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    if (!data?.tenant) return;
    const tenant = data.tenant;
    setBusinessName(tenant.businessName || '');
    setTagline(tenant.tagline || '');
    setDescription(tenant.description || '');
    setPhone(tenant.phone || '');
    setEmail(tenant.email || '');
    setAddress(tenant.address || '');
    setAudienceType(tenant.audienceType || 'UNISEX');
    setLogoPreview(tenant.logo || null);
  }, [data]);

  const saveMut = useMutation({
    mutationFn: () =>
      updateSalonProfile(
        { businessName, tagline, description, phone, address, audienceType },
        logoFile
      ),
    onSuccess: () => {
      showToast(t('toast.salonProfileSaved'), 'success');
      setLogoFile(null);
      queryClient.invalidateQueries({ queryKey: ['salon-profile'] });
    },
    onError: (err) => {
      showToast(err.response?.data?.message || t('errors.saveFailed'), 'error');
    },
  });

  if (isLoading) {
    return (
      <DashboardShell title={t('salon.settings.title')}>
        <p className="text-zinc-600 dark:text-zinc-500">{t('common.loading')}</p>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell title={t('salon.settings.title')} subtitle={t('salon.settings.publicSubtitle')}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          saveMut.mutate();
        }}
        className="max-w-xl space-y-6"
      >
        <div className="flex items-center gap-6">
          <label className="relative h-24 w-24 cursor-pointer overflow-hidden rounded-full border-2 border-zinc-300 dark:border-zinc-700 bg-zinc-100 dark:bg-zinc-800">
            {logoPreview ? (
              <img src={logoPreview} alt={t('salon.settings.logo')} className="h-full w-full object-cover" />
            ) : (
              <span className="flex h-full w-full items-center justify-center text-2xl text-zinc-600 dark:text-zinc-500">
                +
              </span>
            )}
            <input
              type="file"
              accept="image/*"
              className="absolute inset-0 opacity-0"
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;
                setLogoFile(file);
                setLogoPreview(URL.createObjectURL(file));
              }}
            />
          </label>
          <p className="text-sm text-zinc-600 dark:text-zinc-500">{t('salon.settings.uploadLogoHint')}</p>
        </div>

        <Input label={t('salon.settings.salonNameLabel')} id="businessName" value={businessName} onChange={(e) => setBusinessName(e.target.value)} required />
        <Input label={t('salon.settings.tagline')} id="tagline" value={tagline} onChange={(e) => setTagline(e.target.value)} />

        <div>
          <p className="mb-2 text-sm text-zinc-600 dark:text-zinc-400">{t('salon.settings.audienceType')}</p>
          <p className="mb-3 text-xs text-zinc-500">{t('salon.settings.audienceHint')}</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {AUDIENCE_TYPES.map((type) => (
              <label
                key={type}
                className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2.5 text-sm transition ${
                  audienceType === type
                    ? 'border-glow-500 bg-glow-600/10 text-zinc-900 dark:text-zinc-100'
                    : 'border-zinc-300 text-zinc-600 hover:border-zinc-400 dark:border-zinc-700 dark:text-zinc-400'
                }`}
              >
                <input
                  type="radio"
                  name="audienceType"
                  value={type}
                  checked={audienceType === type}
                  onChange={() => setAudienceType(type)}
                  className="accent-glow-600"
                />
                {t(`salon.audience.${type}`)}
              </label>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="description" className="mb-1 block text-sm text-zinc-600 dark:text-zinc-400">
            {t('salon.settings.description')}
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm outline-none focus:border-glow-500"
          />
        </div>
        <Input label={t('common.phone')} id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <Input label={t('common.email')} id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled className="opacity-60" />
        <Input label={t('common.address')} id="address" value={address} onChange={(e) => setAddress(e.target.value)} />

        <div className="rounded-xl border border-dashed border-zinc-300 dark:border-zinc-700 bg-white/50 dark:bg-zinc-900/50 p-4 text-sm text-zinc-600 dark:text-zinc-500">
          {t('salon.settings.hoursPerBranchHint')}{' '}
          <a href="/salon/branches" className="text-glow-400 hover:underline">
            {t('salon.settings.branchManagementLink')}
          </a>{' '}
          {t('salon.settings.hoursPerBranchSuffix')}
        </div>

        <button
          type="submit"
          disabled={saveMut.isPending}
          className="rounded-lg bg-glow-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-glow-500 disabled:opacity-50"
        >
          {saveMut.isPending ? t('common.saving') : t('common.saveChanges')}
        </button>
      </form>

      {canManageBank && (
        <div className="mt-10">
          <BankAccountsManager />
        </div>
      )}
    </DashboardShell>
  );
};

export default SalonSettingsPage;
